import { faArrowRight, faEdit, faEllipsis, faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { Accordion } from 'src/components/Accordion';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Card } from 'src/components/Card';
import { ConfirmDialog } from 'src/components/ConfirmDialog';
import { Dialog } from 'src/components/Dialog';
import { Divider } from 'src/components/Divider';
import { DropdownMenu } from 'src/components/DropdownMenu';
import { EditButton } from 'src/components/EditButton';
import { Grid } from 'src/components/Grid';
import { HtmlTypography } from 'src/components/HtmlTypography';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { ListItem } from 'src/components/ListItem';
import { SearchParamsPagination } from 'src/components/Pagination';
import { SectionHeader } from 'src/components/SectionHeader';
import { SectionToolbar } from 'src/components/SectionToolbar';
import { Stack } from 'src/components/Stack';
import { StickyCard } from 'src/components/StickyCard';
import { DataTable } from 'src/components/Table/DataTable';
import { Typography } from 'src/components/Typography';
import { Note, NoteTypes } from 'src/models/note/_types';
import {
  DateRangeFilter,
  Filters,
  SelectFilter,
} from 'src/shared/Filters/Filters';
import { FormDateTimePicker as DateTimePicker } from 'src/shared/Forms/Fields/FormDateTimePicker';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';
import { PermissionCheck } from 'src/shared/PermissionCheck';
import { useUserNotes } from 'src/shared/useUserNotes';
import { formatFullDate } from 'src/utils/dateFormatter';
import { Identifier } from 'types/utilityTypes';

import { ActionCard } from './ActionCard';


interface Props {
  userId?: Identifier;
  // Should be true when embedded in checkin etc
  embedded?: boolean;
}

export const UserNotes: React.FC<Props> = ({ userId, embedded }) => {
  const { t } = useTranslation('translation', { keyPrefix: 'userNotes' });
  const { t: tCommon } = useTranslation('translation', {
    keyPrefix: 'common.labels',
  });

  const {
    notesLoading,
    notes,
    setNoteToEdit,
    noteToDelete,
    setNoteToDelete,
    noteToEdit,
    formik,
    platformFormik,
    setFieldToDelete,
    fieldToDelete,
    setFieldToEdit,
    fieldToEdit,
    platformFieldToEdit,
    setPlatformFieldToEdit,
    trainingInfoFields,
    userFormik,
    totalEntries,
    perPage,
    sort,
    setSortBy,
    setSortDir,
    sortDir,
    handleDeleteField,
    handleDeleteNote,
    handleClose,

    params,
    updateParam,
    updateFilterParam,
    getFilterParamValue,
    resetFilterParams,
  } = useUserNotes(userId);

  const filtersConfig = [
    {
      field: 'type',
      label: tCommon('Type'),
      Component: SelectFilter,
      options: Object.values(NoteTypes).map((v) => ({ value: v, label: v })),
    },
    {
      field: 'date',
      label: tCommon('Date Range'),
      Component: DateRangeFilter,
    },
  ];

  const columns = [
    {
      id: 'title',
      label: tCommon('Title'),
      sortable: true,
      maxWidth: '200px',
    },
    {
      id: 'notes',
      label: tCommon('Note'),
      sortable: true,
      maxWidth: '300px',
    },
    {
      id: 'date',
      label: tCommon('Date'),
      sortable: true,
      format: formatFullDate,
    },
  ];

  return (
    <>
      <Card sx={{ mb: 6 }}>
        <SectionHeader
          title={t('Notes History')}
          subtitle={t('View history of note edits over time')}
          actions={
            <Button
              onClick={() => {
                setNoteToEdit({
                  id: '',
                  title: '',
                  notes: '',
                  date: new Date(),
                  userId,
                  notableId: undefined,
                  notableType: undefined,
                  createdById: undefined,
                  createdAt: undefined,
                  updatedAt: undefined,
                });
              }}
              startIcon={
                <Icon icon={faPlus} />
              }
              variant="outlined"
            >
              {t('Add')}
            </Button>
          }
        />
        <Divider />
        <SectionToolbar>
          <Filters
            filters={filtersConfig}
            params={params}
            updateParam={updateParam}
            updateFilterParam={updateFilterParam}
            getFilterParamValue={getFilterParamValue}
            resetFilterParams={resetFilterParams}
          />
        </SectionToolbar>
        <Divider />
        <DataTable
          columns={columns}
          rows={notes}
          sortBy={sort}
          setSortBy={setSortBy}
          sortDir={sortDir}
          setSortDir={setSortDir}
          noResultsText={t('No matching notes')}
          loading={notesLoading}
          totalItems={totalEntries}
          itemsPerPage={perPage}
          onClickRow={(row) => setNoteToEdit(row as Note)}
          rowActions={(row) => (
            <IconButton onClick={() => setNoteToEdit(row as Note)}>
              <Icon icon={faEdit} />
            </IconButton>
          )}
        ></DataTable>
        <SearchParamsPagination
          params={params}
          updateParam={updateParam}
          total={totalEntries}
          itemsPerPage={perPage}
        />
      </Card>
      <ConfirmDialog
        open={noteToDelete !== null}
        title={t('Delete Note?')}
        actions={[
          {
            label: t('No'),
            color: 'error',
            variant: 'contained',
            onClick: handleClose,
          },
          {
            label: t('Yes'),
            onClick: handleDeleteNote,
            color: 'secondary',
            variant: 'text',
          },
        ]}
      >
        {t('Are you sure you want to delete this note?')}
      </ConfirmDialog>
      <Dialog
        title={`${noteToEdit?.id ? tCommon('Edit') : tCommon('New')} ${tCommon(
          'Note'
        )}`}
        open={noteToEdit !== null}
        onClose={handleClose}
        rightAdornment={
          <IconButton
            onClick={() => {
              setNoteToEdit(null);
              setNoteToDelete(noteToEdit);
            }}
          >
            <Icon icon={faTrash} />
          </IconButton>
        }
      >
        <Formik {...formik}>
          {({ handleSubmit, isSubmitting }) => (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid
                  size={{
                    xs: 12,
                    md: 12,
                  }}
                >
                  <FormTextField
                    label={tCommon('Title')}
                    name="title"
                    fullWidth
                    required
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 12,
                  }}
                >
                  <FormTextField
                    label={tCommon('Notes')}
                    name="notes"
                    multiline
                    fullWidth
                    required
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 12,
                  }}
                >
                  <DateTimePicker
                    name="date"
                    label={tCommon('Date')}
                    fullWidth
                    required
                  />
                </Grid>
              </Grid>

              <Stack
                direction={{
                  xs: 'column',
                  sm: 'row',
                }}
                flexWrap="wrap"
                spacing={3}
                sx={{ pt: 3 }}
              >
                <Button
                  disabled={isSubmitting}
                  type="submit"
                  variant="contained"
                >
                  {tCommon('Save Note')}
                </Button>
                <Button
                  color="inherit"
                  onClick={() => setNoteToEdit(null)}
                  disabled={isSubmitting}
                >
                  {tCommon('Cancel')}
                </Button>
              </Stack>
            </form>
          )}
        </Formik>
      </Dialog>
      <Card title={t('Client Notes')} sx={{ mb: 6 }}>
        <SectionHeader title={t('Client Notes')} />
        {trainingInfoFields?.map((field) => (
          <Accordion
            key={field.id}
            label={
              <>
                <Typography>{field.label}</Typography>
              </>
            }
          >
            <Formik enableReinitialize {...userFormik}>
              {({ submitForm, values }) => (
                <>
                  {fieldToEdit?.id == field.id ? (
                    <FormTextField
                      label=""
                      value={values.value}
                      name="value"
                      multiline
                      fullWidth
                    />
                  ) : (
                    <HtmlTypography variant="body1" color="text.secondary">
                      {field.value || tCommon('No Notes')}
                    </HtmlTypography>
                  )}
                  <Stack direction="row-reverse" sx={{ mt: 2 }} spacing={1}>
                    <PermissionCheck can="editNoteFields">
                      <DropdownMenu
                        trigger={
                          <IconButton>
                            <Icon
                              icon={faEllipsis}
                            />
                          </IconButton>
                        }
                      >
                        <Box sx={{ p: 1 }}>
                          <ListItem
                            clickable
                            rounded
                            primary={t('Edit global field title')}
                            secondary={t('Change for all clients')}
                            onClick={() => setPlatformFieldToEdit(field)}
                          ></ListItem>
                          <ListItem
                            clickable
                            rounded
                            primary={t('Delete global field')}
                            secondary={t(
                              'Remove this note field for all clients'
                            )}
                            onClick={() => setFieldToDelete(field)}
                          ></ListItem>
                        </Box>
                      </DropdownMenu>
                    </PermissionCheck>
                    <EditButton
                      editing={fieldToEdit?.id == field.id}
                      onClickEdit={() => setFieldToEdit(field)}
                      onClickCancel={() => setFieldToEdit(null)}
                      onClickSave={() => submitForm()}
                    />
                  </Stack>
                </>
              )}
            </Formik>
          </Accordion>
        ))}
      </Card>
      {!embedded && (
        <PermissionCheck can="editNoteFields">
          <StickyCard title={t('Manage')} sx={{ my: 6 }}>
            <Grid container spacing={3}>
              <Grid
                size={{
                  xs: 12,
                  lg: 4,
                }}
              >
                <ActionCard
                  title={t('Add Client Notes Field')}
                  description={t('Add new notes field for all clients')}
                  buttonText={t('Open Dialog')}
                  icon={faArrowRight}
                  onClick={() =>
                    setPlatformFieldToEdit({
                      id: '',
                      label: '',
                      value: '',
                    })
                  }
                />
              </Grid>
            </Grid>
          </StickyCard>
        </PermissionCheck>
      )}
      <ConfirmDialog
        open={fieldToDelete !== null}
        title={t('Delete Field?')}
        actions={[
          {
            label: t('No'),
            color: 'error',
            variant: 'contained',
            onClick: handleClose,
          },
          {
            label: t('Yes'),
            onClick: handleDeleteField,
            color: 'secondary',
            variant: 'text',
          },
        ]}
      >
        {t('Are you sure you want to delete this field?')}
        <br />
        <br />
        <b>{t('It will no longer show on any client profile anymore.')}</b>
      </ConfirmDialog>
      <Dialog
        title={`${
          platformFieldToEdit?.id
            ? tCommon('Edit Field Title')
            : t('Create New Field')
        }`}
        open={platformFieldToEdit !== null}
        onClose={handleClose}
      >
        <Formik {...platformFormik}>
          {({ handleSubmit, isSubmitting }) => (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid
                  size={{
                    xs: 12,
                    md: 12,
                  }}
                >
                  <Typography variant="body2">
                    {t('This field will be {{action}} for all clients.', {
                      action: platformFieldToEdit?.id
                        ? tCommon('updated')
                        : tCommon('created'),
                    })}
                  </Typography>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 12,
                  }}
                >
                  <FormTextField
                    label={tCommon('Label')}
                    name="label"
                    fullWidth
                    required
                  />
                </Grid>
              </Grid>

              <Stack
                direction={{
                  xs: 'column',
                  sm: 'row',
                }}
                flexWrap="wrap"
                spacing={3}
                sx={{ pt: 3 }}
              >
                <Button
                  disabled={isSubmitting}
                  type="submit"
                  variant="contained"
                >
                  {tCommon('Save Field Title')}
                </Button>
                <Button
                  color="inherit"
                  onClick={() => setPlatformFieldToEdit(null)}
                  disabled={isSubmitting}
                >
                  {tCommon('Cancel')}
                </Button>
              </Stack>
            </form>
          )}
        </Formik>
      </Dialog>
    </>
  );
};
